import React, { useState } from "react";
import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  FormField,
  Input,
  Select,
  Spinner,
  SegmentedControl,
  Header,
  Alert,
  Table,
  ButtonDropdown,
  Container
} from "@amzn/awsui-components-react";
import { getReorderableTabs, FIXED_TABS, getTabOrder, applyTabOrderWithFixedTabs } from "../utilities/TabOrderUtils";
import { closePortalDefaultTabs } from "./ClosePortal/constants";
import "./homepage.scss";

const CustomizeTabsModal = ({
  visible,
  onDismiss,
  modalLoading,
  errorText,
  allTabs,
  allCustomTabs,
  hiddenTabs,
  selectedTabControlsSegmentId,
  setSelectedTabControlsSegmentId,
  newTabName,
  setNewTabName,
  selectedUpdateOrDeleteTab,
  setSelectedUpdateOrDeleteTab,
  updateTabNameInput,
  setUpdateTabNameInput,
  selectedTabToUnhide,
  setSelectedTabToUnhide,
  onAddTab,
  onUpdateTab,
  onDeleteTab,
  onUnhideTab,
  visibleTabs,
  onReorderTabs,
  orderedTabs,
  selectedCPView,
  selectedTabType,
  setSelectedTabType

}) => {
  const [reorderableTabsList, setReorderableTabsList] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);

  React.useEffect(() => {
    if (selectedTabControlsSegmentId === 'reorder' && orderedTabs) {
      const { fixedTabs, orderedReorderableTabs } = orderedTabs;
      
      const reorderableTabs = orderedReorderableTabs
      
      setReorderableTabsList(reorderableTabs.map((tab, index) => ({
        ...tab,
        order: index + 3
      })));
    }
  }, [selectedTabControlsSegmentId, orderedTabs, hiddenTabs]);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    let dragIndex = draggedIndex;
    if (dragIndex === null) {
      const dragIndexStr = e.dataTransfer.getData('text/plain');
      dragIndex = parseInt(dragIndexStr, 10);
    }

    if (dragIndex === null || isNaN(dragIndex) || dragIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    if (dragIndex < 0 || dragIndex >= reorderableTabsList.length || 
        dropIndex < 0 || dropIndex >= reorderableTabsList.length) {
      setDraggedIndex(null);
      return;
    }

    const draggedTab = reorderableTabsList[dragIndex];
    const reorderedList = [...reorderableTabsList];

    reorderedList.splice(dragIndex, 1);
    reorderedList.splice(dropIndex, 0, draggedTab);

    const updatedList = reorderedList.map((tab, index) => ({
      ...tab,
      order: index + 3
    }));

    setReorderableTabsList(updatedList);
    setDraggedIndex(null);
  };

  const handleSaveOrder = () => {
    try {
      onReorderTabs(reorderableTabsList);
    } catch (error) {
      console.error('Error in handleSaveOrder:', error);
    }
  };
  return (
    <Modal
      size="large"
      onDismiss={onDismiss}
      visible={visible}
      footer={
        <>
          {errorText && (
            <Alert type="error" statusIconAriaLabel="Error">
              {errorText}
            </Alert>
          )}
          {allTabs?.length >= 1 && (
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={onDismiss}>Cancel</Button>
                {selectedTabControlsSegmentId === 'add' && (
                  <Button variant="primary" onClick={onAddTab}>Add tab</Button>
                )}
                {selectedTabControlsSegmentId === 'update' && (
                  <Button variant="primary" onClick={onUpdateTab}>Update tab</Button>
                )}
                {selectedTabControlsSegmentId === 'delete' && (
                  <Button variant="primary" onClick={onDeleteTab}>Delete tab</Button>
                )}
                {selectedTabControlsSegmentId === 'unhide' && (
                  <Button variant="primary" onClick={onUnhideTab}>Unhide tab</Button>
                )}
                {selectedTabControlsSegmentId === 'reorder' && (
                  <Button variant="primary" onClick={handleSaveOrder}>Save order</Button>
                )}
              </SpaceBetween>
            </Box>
          )}
        </>
      }
      header="Customize Tabs"
    >
      {(allTabs?.length >= 1 && modalLoading === false) ? (
        <>
          <SegmentedControl
            selectedId={selectedTabControlsSegmentId}
            onChange={({ detail }) => setSelectedTabControlsSegmentId(detail.selectedId)}
            label="Modify Tabs"
            options={[
              { text: "Add Tab", id: "add", iconName: "add-plus" },
              { 
                text: "Update Tab", 
                id: "update", 
                iconName: "edit", 
                disabled: allCustomTabs?.length <= 0, 
                disabledReason: "No custom tabs available"
              },
              { 
                text: "Delete Tab", 
                id: "delete", 
                iconName: "remove", 
                disabled: allCustomTabs?.length <= 0, 
                disabledReason: "No custom tabs available"
              },
              { 
                text: "Unhide Tab", 
                id: "unhide", 
                iconName: "unlocked", 
                disabled: hiddenTabs?.length <= 0, 
                disabledReason: "No tabs hidden"
              },
              { 
                text: "Reorder Tabs", 
                id: "reorder", 
                iconName: "menu", 
                disabled: !visibleTabs || visibleTabs?.length <= 1, 
                disabledReason: "Need at least 2 visible tabs to reorder"
              },
            ]}
          />
          
          {selectedTabControlsSegmentId === 'add' && (
            <>
              <Header variant="h3">
                <Box color="text-status-info">Add new custom tab</Box>
              </Header>
              <FormField label="Tab name" stretch>
                <Input 
                  onChange={({ detail }) => setNewTabName(detail.value)}
                  value={newTabName}
                  placeholder='Provide a tab name'
                />
              </FormField>

              <FormField label="Tab type" stretch>
                <Select
                  selectedOption={selectedTabType}
                  onChange={({ detail }) => {
                      setSelectedTabType(detail.selectedOption)
                  }
                  }
                  options={[{label: "Board view", value: "BOARD"}, 
                            {label: "List view", value: "LIST"}]}
                  placeholder='Select tab type to add'
              />
              </FormField>
            </>
          )}
          
          {selectedTabControlsSegmentId === 'update' && (
            <>
              <Header variant="h3">
                <Box color="text-status-info">Update existing custom tab</Box>
              </Header>
              <FormField label="Tab to update" stretch> 
                <Select
                  selectedOption={selectedUpdateOrDeleteTab}
                  onChange={({ detail }) => setSelectedUpdateOrDeleteTab(detail.selectedOption)}
                  options={allCustomTabs?.map((tab) => ({ "label": tab?.label, "value": tab?.id }))}
                  empty="No custom tabs"
                  placeholder='Select document or tracker to update'
                />
              </FormField>
              {selectedUpdateOrDeleteTab && (
                <FormField label="New tab name" stretch>
                  <Input 
                    onChange={({ detail }) => setUpdateTabNameInput(detail.value)}
                    value={updateTabNameInput}
                    placeholder='Provide a tab name' 
                  />
                </FormField>
              )}
            </>
          )}

          {selectedTabControlsSegmentId === 'delete' && (
            <>
              <Header variant="h3">
                <Box color="text-status-info">Remove existing custom tab</Box>
              </Header>
              <FormField label="Tab to remove" stretch>
                <Select
                  selectedOption={selectedUpdateOrDeleteTab}
                  onChange={({ detail }) => setSelectedUpdateOrDeleteTab(detail.selectedOption)}
                  options={allCustomTabs?.map((tab) => ({ "label": tab?.label, "value": tab?.id }))}
                  placeholder='Select custom tab to remove'
                  empty="No custom tabs"
                />
              </FormField>
            </>
          )}

          {selectedTabControlsSegmentId === 'unhide' && (
            <>
              <Header variant="h3">
                <Box color="text-status-info">Unhide hidden custom tab</Box>
              </Header>
              <FormField label="Tab to unhide" stretch>
                <Select
                  selectedOption={selectedTabToUnhide}
                  onChange={({ detail }) => setSelectedTabToUnhide(detail.selectedOption)}
                  options={hiddenTabs?.map((tab) => ({ "label": tab?.label, "value": tab?.id }))}
                  placeholder='Select tab to unhide'
                  empty="No hidden tabs"
                />
              </FormField>
            </>
          )}

          {selectedTabControlsSegmentId === 'reorder' && (
            <>
              <Header variant="h3">
                <Box color="text-status-info">Reorder tabs</Box>
              </Header>
    
              
         

              {reorderableTabsList.length > 0 && (
                <SpaceBetween direction="vertical" size="s">
                  <Box variant="h4" color="text-body-secondary">Reorderable Tabs</Box>
                  <Container>
                    <SpaceBetween direction="vertical" size="xs">
                      <Box variant="p" color="text-body-secondary">
                        Drag and Drop Tabs to Reorder
                      </Box>
                      {reorderableTabsList.map((tab, index) => (
                        <Box key={`reorderable-${tab.id}-${index}`}>
                          <div
                            className="customize-tabs-draggable-item"
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, index)}
                          >
                            <Box className="customize-tabs-drag-handle">
                              ☰
                            </Box>
    
                            <Box className="customize-tabs-label">
                              {tab?.visible ? tab.label : `${tab.label} (Hidden Tab)`}
                            </Box>
                         
                          </div>
                        </Box>
                      ))}
                    </SpaceBetween>
                  </Container>
                </SpaceBetween>
              )}

              {reorderableTabsList.length === 0 && (
                <Box textAlign="center" color="inherit" padding="l">
                  <b>No tabs available to reorder</b>
                </Box>
              )}
            </>
          )}
        </>
      ) : (
        <>
          <Spinner />Loading...
        </>
      )}
    </Modal>
  );
};

export default CustomizeTabsModal;
